import { prefix, isHTMLElement } from "../../js/utilities";

import {
    actions,
    reducers,
    createStore,
    renderState,
    dispatchEvent,
    events,
} from "../../js/state-machine";
import { chevronDown } from "../../js/helpers/built-in-icons";

export const componentName = "MoreLess";

export const componentClasses = Object.freeze({
    styleExpanded: `${prefix}more-less__target--show`,
    styleButton: `.${prefix}more-less__button`,
    styleTarget: `.${prefix}more-less__target`,
});

export const views = {
    trigger: {
        expanded: {
            attrs: { "aria-expanded": "true" },
        },
        collapsed: {
            attrs: { "aria-expanded": "false" },
        },
    },
    target: {
        expanded: {
            classList: [componentClasses.styleExpanded],
        },
        collapsed: {
            classList: [`!${componentClasses.styleExpanded}`],
        },
    },
    list: {
        expanded: {
            attrs: { hidden: null },
        },
        collapsed: {
            attrs: { hidden: "true" },
        },
    },
};

export const eventMap = {
    expand: events.expand(componentName),
    collapse: events.collapse(componentName),
};

export const initialState = {
    expand: false,
};

/**
 *  Removes and adds the 'hidden' attribute to the list items that initially had it
 * @param {NodeList} hiddenItems The NodeList of items that have the 'hidden' attribute upon initialization
 * @param {boolean} isExpanded The state of the component's expansion
 */
export const toggleHiddenListItems = (hiddenItems, isExpanded) => {
    if (!(hiddenItems instanceof NodeList)) {
        return console.error(
            `${componentName}: requires the first parameter to be a NodeList in toggleHiddenItems(NodeList, boolean)`
        );
    }

    if (typeof isExpanded !== "boolean") {
        return console.error(
            `${componentName}: requires the second parameter to be a boolean in toggleHiddenItems(NodeList, boolean)`
        );
    }

    hiddenItems.forEach((item) => {
        renderState(isExpanded ? views.list.expanded : views.list.collapsed, item);
    });
};

export default function MoreLess(element) {
    // Initialization element
    if (!isHTMLElement(element)) {
        return console.error(`"element" must be an HTMLElement. Recieved: ${typeof element}`);
    }

    // If we have already initialized - don't do it again.
    if (element[componentName]) {
        return console.warn(`${componentName} already initialized`);
    }

    let triggerElement = element.querySelector(componentClasses.styleButton);
    let targetElement = element.querySelector(componentClasses.styleTarget);
    const hiddenItems = targetElement.querySelectorAll(`[hidden]`);

    const standAloneButton = element.querySelector(`.dds__more-less__button--standalone`);

    const chevronDownkIconNode = new DOMParser().parseFromString(chevronDown(), "text/html").body
        .firstChild;

    // Inject the chevron down svg into the component
    if (standAloneButton) standAloneButton.appendChild(chevronDownkIconNode.cloneNode(true));

    // If we do not have our required elements, we cannot proceed.
    if (!triggerElement || !targetElement) {
        return console.warn(`${componentName} does not have a trigger and target element.`);
    }

    const toggle = (e) => {
        if (e) e.preventDefault();
        store.dispatch(actions.toggle());
    };

    const handleStateChange = (expand) => {
        renderState(expand ? views.trigger.expanded : views.trigger.collapsed, triggerElement);
        renderState(expand ? views.target.expanded : views.target.collapsed, targetElement);
        if (targetElement.tagName === "OL" || targetElement.tagName === "UL") {
            toggleHiddenListItems(hiddenItems, expand);
        }
        dispatchEvent(expand ? eventMap.expand : eventMap.collapse, element);
    };

    const dispose = function () {
        // Remove event listener
        triggerElement.removeEventListener("click", toggle);

        triggerElement = null;
        targetElement = null;

        // Remove the flag from the element to allow it to be re-created
        element[componentName] = null;
        element = null;
    };

    const expand = () => {
        store.dispatch(actions.expand());
    };

    const collapse = () => {
        store.dispatch(actions.collapse());
    };

    const api = {
        element,
        dispose,
        expand,
        collapse,
        toggle,
    };

    triggerElement.addEventListener("click", toggle);

    const store = createStore(reducers.boolReducer, initialState.expand);
    store.subscribe(handleStateChange);

    element[componentName] = api;

    return api;
}
